// POS-0034 Stage 3: shared phone-identity UI — country selector, approved-backend validation,
// duplicate warning (blocking-grade phone/email, warning-grade name-only), and the shared-phone
// override request. Used by CustomerForm (Customers page + edit + WhatsApp/Email gates) and by
// CustomerPickerModal's quick-add (POS/Quotations) so every surface gets the same experience.
// The selected region lives on `form.phone_region` (not local state) so the caller's final submit
// (validate-before-save, create/update payload, override request) all use the same region.
import { useState, useCallback, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { COUNTRIES, DEFAULT_COUNTRY } from "@/lib/countryDialCodes";
import { PinReasonDialog } from "./PinReasonDialog";

const STATUS_MESSAGES = {
  invalid: "This doesn't look like a valid phone number.",
  contains_letters: "Phone numbers can't contain letters.",
  has_extension: "Extensions aren't supported — enter the direct number.",
  ambiguous_short: "Can't confirm the area code from 7 digits — add the area code if you have it, or save as-is.",
};

export function PhoneIdentitySection({ form, setForm, name, customerId, required, source, testIdPrefix = "phone", seedMatches }) {
  const region = form.phone_region || DEFAULT_COUNTRY;
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [dup, setDup] = useState(null);
  const [overrideOpen, setOverrideOpen] = useState(false);

  const runChecks = useCallback(async (regionOverride) => {
    const phone = (form.phone || "").trim();
    if (!phone) { setStatus(null); setDup(null); return; }
    setChecking(true);
    try {
      const [v, d] = await Promise.all([
        api.get("/customers/phone/validate", { params: { phone, region: regionOverride ?? region } }),
        api.get("/customers/duplicate-check", { params: { phone, name: name || "", exclude_id: customerId || undefined } }),
      ]);
      setStatus(v.data);
      setDup(d.data);
    } catch {
      // Non-blocking: a validation/dup-check failure (e.g. network hiccup) never blocks typing or saving.
    } finally {
      setChecking(false);
    }
  }, [form.phone, name, customerId, region]);

  // POS-0034 Stage 3 fix: a caller that already knows about a duplicate the live pre-check missed
  // (e.g. CustomerPickerModal's own POST-time race backstop, matches from a 409) can seed it here
  // directly instead of forcing the user to re-type/re-blur the phone to discover it again.
  useEffect(() => {
    if (seedMatches && seedMatches.length > 0) setDup({ phone_matches: seedMatches, name_matches: [] });
  }, [seedMatches]);

  const hasPhoneDup = dup?.phone_matches?.length > 0;
  const hasNameDup = !hasPhoneDup && dup?.name_matches?.length > 0;

  return (
    <div className="col-span-2 space-y-1.5" data-testid={`${testIdPrefix}-identity-section`}>
      <Label className="text-xs">Telephone {required ? <span className="text-destructive">*</span> : null}</Label>
      <div className="flex gap-2">
        <select value={region} data-testid={`${testIdPrefix}-region`}
          onChange={(e) => { setForm((f) => ({ ...f, phone_region: e.target.value })); runChecks(e.target.value); }}
          className="h-10 rounded-md border border-input bg-background px-2 text-sm w-28">
          {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
        </select>
        <Input value={form.phone || ""} className="flex-1" placeholder="e.g. 876-292-1234" data-testid={`${testIdPrefix}-input`}
          onChange={(e) => { setForm((f) => ({ ...f, phone: e.target.value, approval_id: undefined })); setDup(null); setStatus(null); }}
          onBlur={() => runChecks()} />
      </div>
      {checking && <p className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-checking`}>Checking…</p>}
      {status && !["blank", "valid"].includes(status.status) && (
        <p className="text-xs text-amber-600" data-testid={`${testIdPrefix}-status-warning`}>{STATUS_MESSAGES[status.status]}</p>
      )}
      {hasPhoneDup && !form.approval_id && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs space-y-1.5" data-testid={`${testIdPrefix}-duplicate-warning`}>
          <p>A customer with this phone already exists: <span className="font-medium">{dup.phone_matches[0].name}</span>.</p>
          <Button type="button" size="sm" variant="outline" onClick={() => setOverrideOpen(true)} data-testid={`${testIdPrefix}-request-override-btn`}>
            Request shared-phone approval
          </Button>
        </div>
      )}
      {hasPhoneDup && form.approval_id && (
        <p className="text-xs text-emerald-600" data-testid={`${testIdPrefix}-override-approved`}>Shared-phone override approved for this save.</p>
      )}
      {hasNameDup && (
        <p className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-name-duplicate-warning`}>
          A customer named "{dup.name_matches[0].name}" already exists — you can still save if they're a different person.
        </p>
      )}
      <PinReasonDialog
        open={overrideOpen}
        onOpenChange={setOverrideOpen}
        title="Authorize shared phone number"
        description={`A Manager or Owner must approve sharing this phone with an existing customer (${form.phone}).`}
        submitLabel="Approve override"
        onConfirm={async (reason, pin) => {
          const { data } = await api.post("/customers/phone-overrides", {
            phone: form.phone, reason, manager_pin: pin, region,
            operation: customerId ? "edit" : "create",
            target_customer_id: customerId || undefined,
            source,
          });
          setForm((f) => ({ ...f, approval_id: data.approval_id }));
          toast.success("Shared-phone override approved. You can now save.");
        }}
      />
    </div>
  );
}
